import { createContext, useContext } from 'react'
import { Link as RouterLink, useLocation, type LinkProps } from 'react-router-dom'
import { getLocale, localizePath, splitLocale, type Locale } from './locales'

export const LocaleContext = createContext<Locale>(getLocale(undefined))

/** The active site locale (code, dir, names...). */
export function useLocale(): Locale {
  return useContext(LocaleContext)
}

/** Current pathname with the locale prefix removed ('/en/pricing' -> '/pricing'). */
export function useLocalPathname(): string {
  const { pathname } = useLocation()
  return splitLocale(pathname).path
}

/** Turn an in-site path into its localized form for the active locale. */
export function useLocalizePath(): (path: string) => string {
  const { code } = useLocale()
  return (path: string) => localizePath(code, path)
}

/**
 * Drop-in replacement for the router Link that keeps the visitor inside the
 * active language: <Link to="/pricing"> renders /en/pricing under /en.
 */
export function Link({ to, ...rest }: LinkProps) {
  const { code } = useLocale()
  const target = typeof to === 'string' ? localizePath(code, to) : to
  return <RouterLink to={target} {...rest} />
}
